/**
 * Ride Inspection timeline: one ordered list of what happened to a ride.
 *
 * Two sources are merged:
 *  1. History: `rides/{id}/timeline/*`, written by the onRideTimeline Cloud
 *     Function on every ride change. It is the only place an Anna's accept and
 *     cancel survive once the ride is re-matched (the server wipes his fields
 *     from the ride document when he cancels).
 *  2. The ride document itself, which always knows the CURRENT state and, for
 *     rides booked before history logging existed, the last Anna cancel and the
 *     last admin rebroadcast.
 *
 * Document-derived steps are dropped when the history already has the same
 * event (same type within DEDUPE_MS), so nothing shows twice, and they fill in
 * for any entry that has not arrived yet.
 *
 * The history stores an Anna only by his `driverId` (no personal data at rest).
 * His name and plate are looked up here from the live drivers list.
 *
 * Pure (no React, no Firebase) so it can be unit tested: node --test
 */
import { toJsDate } from './rides.js';

const DEDUPE_MS = 10000;

const ms = (v) => {
  const d = toJsDate(v);
  const t = d ? d.getTime() : NaN;
  return Number.isFinite(t) ? t : null;
};

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const WAITING_REASON_LABEL = {
  no_location: 'no location from the Anna\'s phone',
  stale_location: 'location was too old',
  foreign_session: 'location came from another phone',
  far: 'arrived too far from the pickup',
  no_proof: 'no proof of arrival',
  no_terms_accepted: 'rider did not accept the terms (older app)',
  quiet_mode: 'quiet mode — not charged',
};

// The reason codes the Anna app's cancel dialog sends (ActiveTripScreen.kt).
const ANNA_CANCEL_REASON = {
  passenger_no_show: 'Passenger not found / no show',
  wrong_location: 'Wrong pickup location',
  vehicle_issue: 'Vehicle issue',
  safety: 'Safety concern',
  other: 'Other',
};

export const annaCancelReason = (code) => ANNA_CANCEL_REASON[code] || code || 'No reason given';

export function formatDuration(totalSec) {
  const s = Math.max(0, Math.round(Number(totalSec) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return rest ? `${m}m ${rest}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export const formatRadius = (m) => {
  const n = num(m);
  if (n == null) return null;
  return n >= 1000 ? `${Math.round((n / 1000) * 10) / 10} km` : `${Math.round(n)} m`;
};

/** What the server recorded at Arrived and at trip start — the evidence behind any waiting charge. */
export function arrivalDetails(ride) {
  const parts = [];
  if (ride.arrivalDistanceM != null) {
    parts.push(`Arrived ${ride.arrivalDistanceM} m from the pickup pin${ride.arrivedFarFromPickup ? ' (flagged far)' : ''}`);
  } else {
    parts.push('Arrival distance not recorded');
  }
  if (ride.waitingCharge != null) {
    parts.push(ride.waitingCharge > 0
      ? `Waited ${ride.waitingMinutes} min · +₹${ride.waitingCharge} waiting charge`
      : `Waited ${ride.waitingMinutes} min · within free time, no charge`);
  } else if (ride.waitingShadow) {
    const w = ride.waitingShadow;
    parts.push(w.eligible
      ? `Waiting ${w.minutes} min · would be ₹${w.wouldCharge} (${WAITING_REASON_LABEL[w.reason] || w.reason})`
      : `No waiting clock: ${WAITING_REASON_LABEL[w.reason] || w.reason}`);
  } else if (ride.waitingStartedAt && ride.status === 'arrived') {
    parts.push('Waiting clock running');
  } else if (ride.waitingIneligibleReason) {
    parts.push(`No waiting clock: ${WAITING_REASON_LABEL[ride.waitingIneligibleReason] || ride.waitingIneligibleReason}`);
  }
  if (ride.waitingPolicy) {
    parts.push(`Terms: ${ride.waitingPolicy.freeMinutes} min free, then ₹${ride.waitingPolicy.perMinute}/min (max ₹${ride.waitingPolicy.maxCharge})`);
  }
  return parts.join(' · ');
}

// Order for entries that share a millisecond, and the group each belongs to.
const TYPE_META = {
  booked: { rank: 0, group: 'trip' },
  dispatch_ring: { rank: 1, group: 'dispatch' },
  offer_sent: { rank: 2, group: 'dispatch' },
  fare_boosted: { rank: 3, group: 'dispatch' },
  rider_kept_waiting: { rank: 4, group: 'dispatch' },
  search_exhausted: { rank: 5, group: 'dispatch' },
  search_retried: { rank: 6, group: 'dispatch' },
  driver_accepted: { rank: 7, group: 'driver' },
  driver_reported_problem: { rank: 8, group: 'driver' },
  driver_arrived: { rank: 9, group: 'trip' },
  trip_started: { rank: 10, group: 'trip' },
  trip_completed: { rank: 11, group: 'trip' },
  driver_cancelled: { rank: 12, group: 'driver' },
  admin_rebroadcast: { rank: 13, group: 'driver' },
  driver_unassigned: { rank: 14, group: 'driver' },
  ride_cancelled: { rank: 15, group: 'trip' },
  rating: { rank: 16, group: 'trip' },
  earlier_cancels: { rank: 12, group: 'driver' },
};

const ACTIVE = ['searching', 'driver_assigned', 'arrived', 'started'];

// Rider-app taps in rides/{id}/events that matter to ops. `kept_waiting` is
// excluded when the server history already records it.
const RIDER_NOTE = {
  driver_called: { title: 'Rider called the Anna', icon: '📞' },
  support_called: { title: 'Rider called support', icon: '☎️' },
  trip_shared: { title: 'Rider shared the trip', icon: '🔗' },
  sos_opened: { title: 'Rider opened SOS', icon: '🆘' },
  sos_action: { title: 'Rider used an SOS option', icon: '🆘' },
  passenger_safe: { title: 'Rider confirmed they are safe', icon: '🛡️' },
  kept_waiting: { title: 'Rider tapped Keep waiting', icon: '⏳' },
};

const who = (e) => e.driverName || 'An Anna';
const plate = (e) => (e.vehicleNumber ? `Vehicle ${e.vehicleNumber}` : null);
const join = (...parts) => parts.filter(Boolean).join(' · ');

function renderEntry(e, ride) {
  switch (e.type) {
    case 'booked': {
      const route = `Pickup: ${ride.pickup?.address || 'Piler'} → Drop: ${ride.destination?.name || ride.destination?.address || 'Destination'}`;
      const fare = num(e.fare) ?? num(ride.fare);
      return {
        title: e.actor === 'admin' ? 'Ride booked by admin (phone booking)' : 'Ride booked',
        subtitle: `${route}${fare != null ? ` (Fare: ₹${fare})` : ''}`,
        icon: '⚡',
        tone: 'done',
      };
    }
    case 'dispatch_ring':
      return {
        title: 'Search widened to the next ring',
        subtitle: join(`Ring ${e.ring}`, formatRadius(e.radiusM) && `within ${formatRadius(e.radiusM)}`),
        icon: '📡',
        tone: 'done',
      };
    case 'offer_sent': {
      const n = num(e.count) || 0;
      // A fare boost offers the ride to the whole service area at once, but the
      // ride doc records it as "ring 1" at that radius. Say what it really is.
      if (e.afterBoost) {
        return {
          title: `Fare boost: offer sent to ${n} more Anna${n === 1 ? '' : 's'}`,
          subtitle: join(
            `Whole service area${formatRadius(e.radiusM) ? ` (${formatRadius(e.radiusM)})` : ''}`,
            e.totalNotified != null && `${e.totalNotified} asked so far`,
          ),
          icon: '📡',
          tone: 'done',
        };
      }
      return {
        title: `Offer sent to ${n} Anna${n === 1 ? '' : 's'}`,
        subtitle: join(
          e.ring != null && `Ring ${e.ring}${formatRadius(e.radiusM) ? ` (${formatRadius(e.radiusM)})` : ''}`,
          e.totalNotified != null && `${e.totalNotified} asked so far`,
        ),
        icon: '📡',
        tone: 'done',
      };
    }
    case 'fare_boosted':
      return {
        title: 'Rider raised the fare',
        subtitle: e.fromFare != null && e.toFare != null ? `₹${e.fromFare} → ₹${e.toFare}` : `Fare now ₹${e.toFare ?? ride.fare}`,
        icon: '💸',
        tone: 'done',
      };
    case 'rider_kept_waiting':
      return {
        title: 'Rider tapped Keep waiting',
        subtitle: 'The search was restarted',
        icon: '⏳',
        tone: 'done',
      };
    case 'search_exhausted': {
      let detail = '';
      if (e.noCaptainsOnline) detail = 'No Anna was online with a usable location';
      else if (e.nearestDriverKm != null) detail = `The nearest available Anna was ${e.nearestDriverKm} km away`;
      return {
        title: 'Every ring tried — no Anna accepted',
        subtitle: detail,
        icon: '⚠️',
        tone: 'warn',
      };
    }
    case 'search_retried':
      return {
        title: 'Search restarted automatically',
        subtitle: 'Offers were sent again to nearby Annas',
        icon: '🔁',
        tone: 'done',
      };
    case 'driver_accepted': {
      // The phone is read live from the ride (it only describes the Anna who holds it now), never stored in the log.
      const phone = e.driverId && e.driverId === ride.driverId && ride.driverPhone ? `Phone: ${ride.driverPhone}` : null;
      return {
        title: e.driverName ? `Anna accepted: ${e.driverName}` : 'An Anna accepted',
        subtitle: join(plate(e), phone, e.sinceSearchStartSec != null && `took ${formatDuration(e.sinceSearchStartSec)} from the start of the search`),
        icon: '👨‍✈️',
        tone: 'done',
      };
    }
    case 'driver_arrived': {
      const current = e.driverId && e.driverId === ride.driverId;
      const own = e.arrivalDistanceM != null
        ? `Arrived ${e.arrivalDistanceM} m from the pickup pin${e.arrivedFarFromPickup ? ' (flagged far)' : ''}`
        : 'Arrival distance not recorded';
      // Waiting-clock evidence lives on the ride and only describes the Anna who holds it now.
      const subtitle = current ? arrivalDetails(ride) : own;
      return {
        title: `${who(e)} arrived at the pickup`,
        subtitle: `${subtitle}${current && ride.status === 'arrived' ? ' · OTP awaiting verification' : ''}`,
        icon: '📍',
        tone: 'done',
      };
    }
    case 'trip_started': {
      const wait = num(e.waitingCharge);
      return {
        title: 'Trip started',
        subtitle: join(
          'PIN verified · heading to the destination',
          wait > 0 && `+₹${wait} waiting charge${e.waitingMinutes != null ? ` (${e.waitingMinutes} min)` : ''}`,
        ),
        icon: '🚀',
        tone: 'done',
      };
    }
    case 'trip_completed': {
      const fare = num(e.fare) ?? num(ride.fare);
      const wait = num(e.waitingCharge) ?? num(ride.waitingCharge);
      return {
        title: e.byAdmin ? 'Ride force-completed by admin' : 'Ride completed',
        subtitle: `${fare != null ? `₹${fare} ` : ''}${e.byAdmin ? 'recorded as completed' : 'cash collected by the Anna'}${wait > 0 ? ` (includes ₹${wait} waiting)` : ''}`,
        icon: '🏁',
        tone: 'done',
      };
    }
    case 'driver_cancelled':
      return {
        title: `${who(e)} cancelled after accepting`,
        subtitle: join(
          `Reason: ${annaCancelReason(e.reason)}`,
          e.fromStatus === 'arrived' ? 'was already at the pickup' : 'was on the way',
          e.heldForSec != null && `held the ride for ${formatDuration(e.heldForSec)}`,
          plate(e),
          'the rider is being re-matched',
        ),
        icon: '🚫',
        tone: 'cancelled',
      };
    case 'earlier_cancels':
      return {
        title: `${e.count} earlier Anna${e.count === 1 ? '' : 's'} also cancelled`,
        subtitle: 'Names, times and reasons were not recorded for this ride',
        icon: '🚫',
        tone: 'cancelled',
      };
    case 'admin_rebroadcast':
      return {
        title: 'Admin rebroadcast the ride',
        subtitle: join(e.driverName && `${e.driverName} was unassigned`, 'nearby Annas were notified again'),
        icon: '📢',
        tone: 'warn',
      };
    case 'driver_unassigned':
      return {
        title: `${who(e)} was unassigned`,
        subtitle: 'The ride went back to searching',
        icon: '↩️',
        tone: 'warn',
      };
    case 'driver_reported_problem':
      return {
        title: `${who(e)} reported a problem`,
        subtitle: e.note || 'No note',
        icon: '🛠️',
        tone: 'warn',
      };
    case 'ride_cancelled': {
      const actor = e.actor || 'rider';
      let title = 'Ride cancelled by the rider';
      if (actor === 'driver') title = 'Ride cancelled by the Anna';
      else if (actor === 'admin') title = 'Ride cancelled by admin';
      else if (actor === 'system') title = e.autoExpired ? 'Search expired — no Anna was available' : 'Ride cancelled by the system';
      return {
        title,
        subtitle: join(
          `Reason: ${e.reason || 'Cancelled by user'}`,
          e.driverName && `${e.driverName} had accepted${e.afterAcceptSec != null ? ` ${formatDuration(e.afterAcceptSec)} earlier` : ''}`,
        ),
        icon: '❌',
        tone: 'cancelled',
      };
    }
    case 'rating':
      return {
        title: `Passenger rating: ${e.rating}★`,
        subtitle: `Tags: [${(e.ratingTags || []).join(', ') || 'clean ride'}]${e.ratingComment ? ` · "${e.ratingComment}"` : ''}`,
        icon: '⭐',
        tone: 'done',
      };
    default:
      // A type this admin build does not know yet: still show it, never hide it.
      return {
        title: String(e.type || 'event').replace(/_/g, ' '),
        subtitle: '',
        icon: '•',
        tone: 'done',
      };
  }
}

/** Entries implied by the ride document alone (current state + the last cancel/rebroadcast it still remembers). */
export function entriesFromRide(ride) {
  const out = [];
  const add = (type, atMs, extra = {}) => out.push({ type, atMs, source: 'ride', ...extra });

  add('booked', ms(ride.createdAt), { fare: ride.fare, actor: String(ride.customerId || '').startsWith('manual_') ? 'admin' : 'rider' });

  const accepted = ms(ride.acceptedAt) ?? ms(ride.assignedAt);
  if (ride.driverId && (accepted != null || ride.status === 'driver_assigned')) {
    add('driver_accepted', accepted ?? ms(ride.updatedAt), {
      driverId: ride.driverId, driverName: ride.driverName, vehicleNumber: ride.vehicleNumber,
    });
  }
  if (ride.driverId && ms(ride.arrivedAt) != null) {
    add('driver_arrived', ms(ride.arrivedAt), {
      driverId: ride.driverId, driverName: ride.driverName,
      arrivalDistanceM: ride.arrivalDistanceM, arrivedFarFromPickup: ride.arrivedFarFromPickup,
    });
  }
  if (ride.driverId && ms(ride.startedAt) != null) {
    add('trip_started', ms(ride.startedAt), {
      driverId: ride.driverId, waitingCharge: ride.waitingCharge, waitingMinutes: ride.waitingMinutes,
    });
  }
  if (ride.status === 'completed') {
    add('trip_completed', ms(ride.completedAt) ?? ms(ride.updatedAt), {
      driverId: ride.driverId, fare: ride.fare, waitingCharge: ride.waitingCharge,
      byAdmin: ride.completedByAdmin === true,
    });
    if (ride.rating) {
      add('rating', ms(ride.ratedAt) ?? ms(ride.completedAt), {
        rating: ride.rating, ratingTags: ride.ratingTags, ratingComment: ride.ratingComment,
      });
    }
  }
  if (ride.status === 'cancelled') {
    add('ride_cancelled', ms(ride.cancelledAt) ?? ms(ride.updatedAt), {
      actor: ride.cancelledByAdmin === true ? 'admin' : (ride.cancelledBy === 'passenger' || !ride.cancelledBy ? 'rider' : ride.cancelledBy),
      reason: ride.cancelReason, autoExpired: ride.autoExpired === true,
      driverName: ride.driverName,
    });
  }
  // Only the MOST RECENT Anna cancel / rebroadcast survives on the ride.
  if (ms(ride.lastCancelledByDriverAt) != null) {
    add('driver_cancelled', ms(ride.lastCancelledByDriverAt), {
      driverId: ride.reassignedFromDriverId, driverName: ride.lastCancelledDriverName,
      reason: ride.lastDriverCancelReason,
    });
  }
  if (ms(ride.rebroadcastAt) != null) {
    add('admin_rebroadcast', ms(ride.rebroadcastAt), { actor: 'admin' });
  }
  if (ms(ride.fareBoostedAt) != null) {
    add('fare_boosted', ms(ride.fareBoostedAt), { toFare: ride.fare });
  }
  return out;
}

const normaliseHistory = (entries) => (Array.isArray(entries) ? entries : []).map((e) => ({
  ...e, atMs: ms(e.at), source: 'log',
}));

/**
 * Fill an entry's Anna name and plate from the live drivers list. With no list
 * loaded yet he is just "Anna …1234"; with a list that lacks him he has been
 * deleted (or never existed), which is worth saying plainly.
 */
function resolveDriver(entry, drivers) {
  if (!entry.driverId) return entry;
  const known = Array.isArray(drivers) && drivers.length > 0;
  const d = known ? drivers.find((x) => x.id === entry.driverId) : null;
  const tail = String(entry.driverId).slice(-4);
  return {
    ...entry,
    driverName: entry.driverName || d?.name || (known ? `Deleted or unknown Anna …${tail}` : `Anna …${tail}`),
    vehicleNumber: entry.vehicleNumber || d?.vehicleNumber || null,
  };
}

/**
 * @param {object} ride        the ride document (with id)
 * @param {object[]} history   rides/{id}/timeline docs
 * @param {object[]} riderEvents rides/{id}/events docs (rider-app taps)
 * @param {object[]} drivers   live Annas (id, name, vehicleNumber) to name driverIds
 * @returns {{ steps: object[], complete: boolean, notice: string|null }}
 */
export function buildRideTimeline(ride, history = [], riderEvents = [], drivers = []) {
  if (!ride) return { steps: [], complete: true, notice: null };

  const logged = normaliseHistory(history);
  const complete = logged.some((e) => e.type === 'booked');

  const fromRide = entriesFromRide(ride).filter((s) => !logged.some(
    (e) => e.type === s.type && (s.atMs == null || e.atMs == null || Math.abs(e.atMs - s.atMs) <= DEDUPE_MS),
  ));

  // For a ride with no full history, the other Annas who cancelled are known
  // only by count (cancelledDriverIds), never by name or time.
  const extras = [];
  if (!complete) {
    const cancelledCount = Array.isArray(ride.cancelledDriverIds) ? new Set(ride.cancelledDriverIds).size : 0;
    const known = logged.filter((e) => e.type === 'driver_cancelled').length
      + fromRide.filter((e) => e.type === 'driver_cancelled').length;
    if (cancelledCount > known) {
      const lastCancel = fromRide.find((e) => e.type === 'driver_cancelled');
      extras.push({
        type: 'earlier_cancels', count: cancelledCount - known, source: 'ride',
        atMs: lastCancel?.atMs != null ? lastCancel.atMs - 1 : null,
      });
    }
  }

  const notes = (Array.isArray(riderEvents) ? riderEvents : [])
    .filter((e) => RIDER_NOTE[e.type] && !(complete && e.type === 'kept_waiting'))
    .map((e) => ({ type: `note:${e.type}`, atMs: ms(e.at), source: 'event', raw: e }));

  const merged = [...logged, ...fromRide, ...extras, ...notes].map((e) => resolveDriver(e, drivers));

  // Sort by time; entries with no time stay beside the entry before them.
  let carry = 0;
  const keyed = merged.map((e, i) => {
    const t = e.atMs != null ? e.atMs : carry;
    carry = t;
    const rank = e.source === 'event' ? 100 : (TYPE_META[e.type]?.rank ?? 50);
    return { e, t, rank, i };
  });
  keyed.sort((a, b) => a.t - b.t || a.rank - b.rank || a.i - b.i);

  // The first offer after a fare boost is the boost's whole-area broadcast.
  let afterBoost = false;
  keyed.forEach((k) => {
    if (k.e.type === 'fare_boosted') afterBoost = true;
    else if (k.e.type === 'offer_sent' || k.e.type === 'dispatch_ring') {
      if (afterBoost && k.e.type === 'offer_sent' && Number(k.e.ring) === 1) k.e = { ...k.e, afterBoost: true };
      afterBoost = false;
    }
  });

  const steps = keyed.map(({ e, i }) => {
    if (e.source === 'event') {
      const meta = RIDER_NOTE[e.raw.type];
      const detail = e.raw.action || e.raw.via || null;
      return {
        id: `note-${e.raw.id || i}`, type: e.type, group: 'note', atMs: e.atMs,
        title: meta.title, subtitle: detail ? String(detail).replace(/_/g, ' ') : '', icon: meta.icon, tone: 'note',
      };
    }
    const r = renderEntry(e, ride);
    return {
      id: `${e.source}-${e.id || e.type}-${i}`, type: e.type, group: TYPE_META[e.type]?.group || 'trip',
      atMs: e.atMs, ...r,
    };
  });

  // A ride that is still searching: the live ring state comes from the ride
  // document, which is always current (history only records what already happened).
  if (ride.status === 'searching') {
    const ring = ride.dispatchRing || 1;
    const radius = formatRadius(ride.dispatchRadiusM);
    const asked = Array.isArray(ride.notifiedDriverIds) ? ride.notifiedDriverIds.length : null;
    steps.push({
      id: 'live-searching', type: 'searching', group: 'dispatch', atMs: null,
      title: 'Looking for an Anna',
      subtitle: join(
        `Ring ${ring}${radius ? ` (${radius})` : ''}`,
        asked != null && `${asked} Anna${asked === 1 ? '' : 's'} asked so far`,
        ride.searchExhausted && 'every ring tried, retrying',
      ),
      icon: '📡', tone: 'active',
    });
  } else if (ACTIVE.includes(ride.status)) {
    // The newest real step of an in-progress ride is the one happening now.
    for (let i = steps.length - 1; i >= 0; i -= 1) {
      if (steps[i].tone === 'note') continue;
      if (steps[i].tone === 'done') steps[i].tone = 'active';
      break;
    }
  }

  const notice = complete ? null
    : 'This ride was booked before history logging started, so only its latest Anna cancel and rebroadcast are still on record.';
  return { steps, complete, notice };
}

/** The steps that describe dispatch (rings, offers, boosts, retries) for the Dispatch tab. */
export const dispatchSteps = (steps) => steps.filter((s) => s.group === 'dispatch' && s.id !== 'live-searching');

